import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  PlusCircle, ChevronLeft, Server, Globe, Database, 
  HardDrive, Cloud, Shield, Layers, ChevronDown
} from 'lucide-react';
import { addDatabase, updateDatabase, deleteDatabase, setProject , deleteBackendService, deleteFrontendApp} from '../../actions/infrastructures';
import AppModal from '../../components/Modals/Client/AppModal';
import ServiceModal from '../../components/Modals/Server/ServiceModal';
import DatabaseModal from '../../components/Modals/Database/DatabaseModal';
import ResourceCard from '../../components/Card/ResourceCard';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isAppModalOpen, setAppModalOpen] = useState(false);
  const [isDatabaseModalOpen, setDatabaseModalOpen] = useState(false);
  const [isServiceModalOpen, setServiceModalOpen] = useState(false);
  const [serverToEdit, setServerToEdit] = useState(null);
  const [databaseToEdit, setDatabaseToEdit] = useState(null);
  
  useEffect(() => {
    const fetchProject = async () => {
      if (projectId) {
        await dispatch(setProject(projectId));
      }
    };
  
    fetchProject();
  }, [dispatch, projectId]);

  
  const project = useSelector(state => state?.infrastructures?.currentProject);
  const resources = [
    ...(project?.frontendApps || []).map(app => ({
      id: app.appId,
      type: 'Frontend App',
      name: app.appName,
      status: app.status,
      createdAt: app.createdAt
    })),
    ...(project?.backendServices || []).map(service => ({
      id: service.serviceId,
      type: 'Backend Service',
      name: `${service.gitHubRepo}-${service.branchUsed}`,
      gitHubRepo: service.gitHubRepo,
      branchUsed: service.branchUsed,
      status: service.status,
      createdAt: service.createdAt
    })),
    ...(project?.databases || []).map(db => ({
      id: db.databaseId,
      type: 'Database',
      name: db.databaseName,
      status: db.status,
      createdAt: db.createdAt
    }))
  ];

  const projectName = `${project?.projectName}-${project?.environment}`

  const resourceOptions = [
    { label: 'Frontend App', value: 'frontendApp', icon: <Globe className="w-4 h-4" /> },
    { label: 'Backend Service', value: 'backendService', icon: <Server className="w-4 h-4" /> },
    { label: 'Database', value: 'database', icon: <Database className="w-4 h-4" /> },
    { label: 'Redis Cluster', value: 'redisCluster', icon: <HardDrive className="w-4 h-4" /> },
    { label: 'File Storage', value: 'fileStorage', icon: <Cloud className="w-4 h-4" /> }
  ];

  const handleResourceClick = (resourceId, resourceType) => {
    // Navigate to resource details
    if (resourceType === 'Frontend App') {
      navigate(`/project/${projectId}/frontend-app/${resourceId}`);
    } else if (resourceType === 'Backend Service') {
      navigate(`/project/${projectId}/backend-service/${resourceId}`);
    } else if (resourceType === 'Database') {
      navigate(`/project/${projectId}/database/${resourceId}`);
    } else if (resourceType === 'Redis Cluster') {
      navigate(`/project/${projectId}/redis-cluster/${resourceId}`);
    } else if (resourceType === 'File Storage') {
      navigate(`/project/${projectId}/file-storage/${resourceId}`);
    }
  };

  const handleResourceTypeSelect = (resourceType) => {
    setDropdownOpen(false);
    
    if (resourceType === 'frontendApp') {
      setAppModalOpen(true);
    } else if (resourceType === 'backendService') {
      setServiceModalOpen(true);
    } else if (resourceType === 'database') { 
      setDatabaseModalOpen(true);
    } else {
      console.log(`Creating new ${resourceType}`);
      // Handle other resource types
    }
  };

  const getStatusSummary = () => {
    const total = resources.length;
    const active = resources.filter(r => 
      r.status?.toLowerCase() === 'active' || r.status?.toLowerCase() === 'running'
    ).length;
    const pending = resources.filter(r => 
      r.status?.toLowerCase() === 'pending' || r.status?.toLowerCase() === 'deploying'
    ).length;
    
    return { total, active, pending };
  };

  const { total, active, pending } = getStatusSummary();

  const getProjectName = () => {
    
    return `${project?.projectName}-${project?.environment}`;
  };


  const handleEdit = (resource) => {
    if (resource.type === 'Frontend App') {
      // setServerToEdit(resource);
      // setServiceModalOpen(true);
    } else if (resource.type === 'Backend Service') {
      setServerToEdit(resource);
      setServiceModalOpen(true);
    } else if(resource.type === 'Database') {
      setDatabaseToEdit(resource);
      setDatabaseModalOpen(true);
    }
  };

  const handleDatabaseSubmit = async (projectId, databaseData) => {
    try {
      if(databaseToEdit) {
        await dispatch(updateDatabase(projectId, databaseToEdit.id, databaseData));
      } else {
        await dispatch(addDatabase(projectId, databaseData));
      }
    } catch (error) {
      console.log('Error creating database:', error);
    }
  };
  
  const handleDelete = async (resourceId, resourceType) => {
    console.log(`Deleting ${resourceType} with ID: ${resourceId}`);
    if(resourceType === 'Frontend App') {
      try {
        await dispatch(deleteFrontendApp(projectId, resourceId));
      } catch (error) {
        console.log(`Error deleting ${resourceType} with ID: ${resourceId}`, error);
      }
    } else if(resourceType === 'Backend Service') {
      try {
        console.log(`Deleting ${resourceType} with ID: ${resourceId}`);
        await dispatch(deleteBackendService(projectId, resourceId));
      } catch (error) {
        console.log(`Error deleting ${resourceType} with ID: ${resourceId}`, error);
      }
    } else if(resourceType === 'Database') {
      try {
        console.log(`Deleting ${resourceType} with ID: ${resourceId}`);
        await dispatch(deleteDatabase(projectId, resourceId));
      } catch (error) {
        console.log(`Error deleting ${resourceType} with ID: ${resourceId}`, error);
      }
    }
  };
  const handleServiceModalClose = () => {
    setServiceModalOpen(false);
    setServerToEdit(null);
  };

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Layers className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{getProjectName()}</h1>
            </div>
            
            <div className="flex flex-wrap gap-2 items-center">
              <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                <Shield className="w-4 h-4 inline mr-1" />
                {project?.status}
              </span>
              <span className="text-sm text-gray-500">
                Created at: {project?.createdAt}
              </span>
            </div>
          </div>
          
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              <PlusCircle className="w-5 h-5" />
              <span>Add Resource</span>

            </button>
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-10 border border-gray-100 py-1">
                {resourceOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleResourceTypeSelect(option.value)}
                    className="flex items-center space-x-3 w-full px-4 py-2 hover:bg-gray-50 text-gray-700 first:rounded-t-lg last:rounded-b-lg transition-colors duration-200"
                  >
                    <span className="text-gray-500">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Resource Summary Section */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-8">
        <h2 className="text-lg font-semibold mb-4">Resource Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-lg p-4">
            <span className="text-sm text-indigo-800">Total Resources</span>
            <p className="text-2xl font-bold text-indigo-700">{total}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <span className="text-sm text-green-800">Active Resources</span>
            <p className="text-2xl font-bold text-green-700">{active}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <span className="text-sm text-yellow-800">Pending Resources</span>
            <p className="text-2xl font-bold text-yellow-700">{pending}</p>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-4">Resources</h2>
        {resources.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-200">
            <p className="text-gray-500 mb-4">No resources found in this project</p>
            <button
              onClick={() => setDropdownOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
            >
              <PlusCircle className="w-5 h-5 mr-2" /> 
              Add Your First Resource
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map(resource => (
              <ResourceCard 
                key={resource.id} 
                resource={resource}
                onClick={() => handleResourceClick(resource.id, resource.type)}
                onDelete={() => handleDelete(resource.id, resource.type)}
                onEdit={() => handleEdit(resource)}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Modals */}
      <AppModal 
        isOpen={isAppModalOpen} 
        onClose={() => setAppModalOpen(false)} 
      />
      
      <ServiceModal 
        isOpen={isServiceModalOpen} 
        onClose={handleServiceModalClose}
        serverToEdit={serverToEdit}
        setServerToEdit={setServerToEdit}
        projectName={projectName}
      /> 
      <DatabaseModal
        isOpen={isDatabaseModalOpen}
        onClose = {() => {
          setDatabaseModalOpen(false);
          setDatabaseToEdit(null);
        }}
        onSubmit={handleDatabaseSubmit}
        initialData={databaseToEdit}
      />
    </div>
  );
};

export default ProjectDetails;